/* eslint-disable no-redeclare */
import {
  City,
  County,
  CountyFips,
  Decimal,
  DomainSessionId,
  DomainUserId,
  Email,
  FirstName,
  FPL,
  Gender,
  HealthcareSessionId,
  HealthcareUserId,
  HouseholdIncome,
  HouseholdSize,
  Integer,
  IpAddress,
  LastName,
  MaritalStatus,
  NonEmptyText,
  Source,
  SubProductType,
  TrueFalse,
  Url,
  USAStateAcronym,
  UUID,
  YesNo,
  ZipCode,
} from '@hc-frontend/core-types';
import { z } from 'zod';

export const BLANK = z.literal('');

const DateText = z.string();

export const FamilyMemberDto = z.object({
  dateOfBirth: DateText.optional(),
  firstName: FirstName.optional(),
  lastName: LastName.optional(),
  gender: Gender.optional(),
  tobaccoUser: z.boolean().optional(),
});

export type FamilyMemberDto = z.infer<typeof FamilyMemberDto>;

export const FamilyChild = FamilyMemberDto.extend({
  journeyMemberId: z.enum(['Child_1', 'Child_2', 'Child_3', 'Child_4']),
  relationship: z.literal('CHILD'),
});
export type FamilyChild = z.infer<typeof FamilyChild>;

export const FamilySpouse = FamilyMemberDto.extend({
  journeyMemberId: z.literal('Spouse'),
  relationship: z.literal('SPOUSE'),
});
export type FamilySpouse = z.infer<typeof FamilySpouse>;

export const FamilyDto = FamilyChild.or(FamilySpouse);
export type FamilyDto = z.infer<typeof FamilyDto>;

export const WhenYouNeedMedicareCoverage = z.enum([
  'right_away',
  'next_month_or_later',
  'not_sure',
]);

export const WhenYouNeedMedicareCoverageOrBlank = z.union([
  WhenYouNeedMedicareCoverage,
  BLANK,
]);

export type WhenYouNeedMedicareCoverageOrBlank = z.infer<
  typeof WhenYouNeedMedicareCoverageOrBlank
>;

export const EnrolledPartAPartB = z.enum(['yes', 'no', 'not_sure']);

export const KindOfMedicarePlan = z.enum([
  'medicare_advantage',
  'medicare_supplement_medigap',
  'not_sure',
]);

export const InsureWho = z.enum(['Just me', 'Myself and others']);

export type InsureWho = z.infer<typeof InsureWho>;

export const VisitPurpose = z.enum([
  'eligibility_check',
  'current_plan_assistance',
  'switch_plan',
  'first_time_enrollment',
  'other',
]);

export type VisitPurpose = z.infer<typeof VisitPurpose>;

export const EstimatedSpending = z.enum(['low', 'medium', 'high']);

export type EstimatedSpending = z.infer<typeof EstimatedSpending>;

export const QualifyingLifeEvent = z.enum([
  'lost-coverage',
  'got-new-job',
  'moved-new-state',
  'had-a-baby',
  'none-of-these',
]);

export type QualifyingLifeEvent = z.infer<typeof QualifyingLifeEvent>;

export const CoverageDuration = z.enum(['Permanent', 'Temporary', 'Not sure']);

export type CoverageDuration = z.infer<typeof CoverageDuration>;

export const SeniorCheck = z.enum(['O65', 'U65']);

export type SeniorCheck = z.infer<typeof SeniorCheck>;

export const EnrolledPartAPartBOrBlank = z.union([EnrolledPartAPartB, BLANK]);
export type EnrolledPartAPartBOrBlank = z.infer<
  typeof EnrolledPartAPartBOrBlank
>;

export const KindOfMedicarePlanOrBlank = z.union([KindOfMedicarePlan, BLANK]);
export type KindOfMedicarePlanOrBlank = z.infer<
  typeof KindOfMedicarePlanOrBlank
>;

export const ImmediateCoverage = z.enum(['immediately', '2_weeks', 'not_sure']);
export const ImmediateCoverageOrBlank = z.union([ImmediateCoverage, BLANK]);
export type ImmediateCoverageOrBlank = z.infer<typeof ImmediateCoverageOrBlank>;

export const Income = z.enum(['below', 'between', 'over']);
export type Income = z.infer<typeof Income>;
export const IncomeOrBlank = z.union([Income, BLANK]);
export type IncomeOrBlank = z.infer<typeof IncomeOrBlank>;

export const QuestionAnswersDto = z.object({
  when_you_need_medicare_coverage: WhenYouNeedMedicareCoverageOrBlank,
  enrolled_part_a_part_b: EnrolledPartAPartBOrBlank,
  kind_of_medicare_plan: KindOfMedicarePlanOrBlank,
  county_fips: CountyFips,
  medicaid_eligible: z.enum([
    'qualified',
    'unqualified',
    'no-medicaid-no-subsidy',
    'medicaid',
  ]),
  'is-oep': z.enum(['is-oep-no', 'is-oep-yes']),
  is_phoenix: TrueFalse,
  is_under_65: z.enum(['U65', 'O65']),
  askForZipCode: TrueFalse,
  age: Integer,
  tobacco: YesNo,
  immediate_coverage_q: ImmediateCoverageOrBlank,
  coverage_duration: z.enum(['Permanent', 'Temporary', 'Not sure']),
  estimated_spending: EstimatedSpending,
  'subsidy-qualify': z.enum([
    'qualified',
    'unqualified',
    'medicaid',
    'no-medicaid-no-subsidy',
  ]),
  'subsidy-check': z.number(),
  'subsidy-value': z.number(),
  income: IncomeOrBlank,
  fpl: Decimal,
  'quality-life-event': z.enum([
    'got-new-job',
    'lost-coverage',
    'moved-new-state',
    'had-a-baby',
    'none-of-these',
  ]),
  insure_who: InsureWho,
  mobile: z.enum(['y', 'n']),
  visit_purpose: VisitPurpose,
});

export type QuestionAnswersDto = z.infer<typeof QuestionAnswersDto>;

/**
 * @See https://github.com/healthcarecom/lead-storage-service/blob/main/src/main/java/com/healthcare/backend/leadstorageservice/lead/service/dto/UpsertProspectDto.java
 */
export const UpdateProspectBaseDto = z
  .object({
    sessionId: HealthcareSessionId,
    userId: HealthcareUserId,
    domainUserId: DomainUserId,
    domainSessionId: DomainSessionId,
    standardizedMaterialId: NonEmptyText,
    productTypeId: z.number().min(2).max(6),
    subProductType: SubProductType.optional(),
    src: Source,
    firstName: FirstName,
    lastName: LastName,
    dateOfBirth: DateText,
    state: USAStateAcronym,
    email: Email,
    maritalStatus: MaritalStatus,
    householdSize: HouseholdSize,
    gender: Gender,
    householdIncome: HouseholdIncome,
    address: z.string().optional(),
    city: City,
    county: County,
    countyFips: CountyFips,
    zip: ZipCode,
    landingUrl: Url,
    landingPage: Url,
    ipAddress: IpAddress,
    journeyId: Integer,
    fpl: FPL,
    tobaccoUser: z.boolean(),
    tcpaConsent: NonEmptyText,
    tcpaUserAgent: NonEmptyText,
    tcpaUniversalId: UUID,
    tcpaSite: NonEmptyText,
    tcpaSession: NonEmptyText,
    trustedFormUrl: Url,
    language: NonEmptyText,
    traffic: z.enum(['INTERNAL', 'EXTERNAL']),
    family: FamilyDto.array(),
    phoneNumber: z.string().regex(/^\d{10}$/),
    areaCode: z.string().regex(/^\d{3}$/),
    additionalQuestions: QuestionAnswersDto.partial(),
  })
  .partial()
  .required({
    sessionId: true,
    userId: true,
  });

export type UpdateProspectBaseDto = z.infer<typeof UpdateProspectBaseDto>;

export const UpdateProspectDto = UpdateProspectBaseDto.required({
  userId: true,
  sessionId: true,
});

export type UpdateProspectDto = z.infer<typeof UpdateProspectDto>;
